import { Header, Message } from '@mairistem/composants';
import { ListeTable } from '@mairistem/liste';
import dayjs from 'dayjs';
import _ from 'lodash';
import React from 'react';
import { ETATS_CYCLE, MODES_REGLEMENTS } from '@/constants';
import { ModeReglementContext, RegieContext } from '@/contexts';
import type { CycleType } from '@/types';
import { formatMonetaire } from '@/utils';

interface CyclesListeTableRowProps {
  item: CycleType & { dateLimiteProlongee?: string | null };
  rowProps: Record<string, unknown>;
}

const CyclesListeTableRow = (props: CyclesListeTableRowProps) => {
  const { item, rowProps } = props;

  const { regies, selectedRegie } = React.useContext(RegieContext);
  const { regieModesReglement } = React.useContext(ModeReglementContext);

  // Si 1 seul logiciel facturier, on masque la colonne
  const hideSourceColumn =
    _.size(_.find(regies, { identifiant: selectedRegie })?.logicielsFacturier) <= 1;

  const hasPrelevement = React.useMemo(
    () => _.some(regieModesReglement, { code: MODES_REGLEMENTS.PRELEVEMENT }),
    [regieModesReglement],
  );

  const {
    identifiant,
    titre,
    dateIncorporation,
    dateEmission,
    dateLimite,
    dateLimiteProlongee,
    datePrelevement,
    logicielFacturier: { code },
    nombreFactures,
    nombreFacturesPrelevees,
    montant,
    montantEncaisse,
    etat,
  } = item;

  const cyleDateEmission = `${titre}${dateEmission ? ` - ${dayjs(dateEmission).format('DD/MM/YYYY')}` : ''}`;

  return (
    <ListeTable.Row key={identifiant} {...rowProps}>
      <ListeTable.Cell
        name="dateIncorporation"
        textAlign="center"
        // @ts-expect-error
        title={dateIncorporation}
        style={{ width: 175 }}
      >
        {dateIncorporation ? dayjs(dateIncorporation).format('DD/MM/YYYY') : '-'}
      </ListeTable.Cell>
      <ListeTable.Cell
        name="titre"
        // @ts-expect-error
        title={cyleDateEmission}
      >
        {cyleDateEmission}
      </ListeTable.Cell>
      {!hideSourceColumn && (
        <ListeTable.Cell
          name="logicielFacturier"
          textAlign="center"
          // @ts-expect-error
          title={code}
          style={{ width: 90 }}
        >
          {code}
        </ListeTable.Cell>
      )}
      <ListeTable.Cell
        name="dateLimite"
        textAlign="center"
        {...(dateLimiteProlongee && {
          title: `Date limite non prolongée : ${dayjs(dateLimite).format('DD/MM/YYYY')}`,
        })}
        style={{ width: 175 }}
      >
        {(() => {
          const dateLimiteEffective = dateLimiteProlongee || dateLimite;
          const joursRestants = dayjs(dateLimiteEffective).diff(dayjs(), 'day');
          const estDans7DerniersJours = joursRestants >= 0 && joursRestants < 7;
          const dateAffichee = dayjs(dateLimiteEffective).format('DD/MM/YYYY');

          return estDans7DerniersJours ? (
            <Header as="h5" color="orange" style={{ fontWeight: 600 }}>
              {dateAffichee}
            </Header>
          ) : (
            dateAffichee
          );
        })()}
      </ListeTable.Cell>
      <ListeTable.Cell
        name="nombreFactures"
        // @ts-expect-error
        title={nombreFactures}
        style={{ width: 120 }}
      >
        {nombreFactures}
      </ListeTable.Cell>
      <ListeTable.Cell
        name="montant"
        textAlign="right"
        // @ts-expect-error
        title={formatMonetaire(montant)}
        style={{ width: 140 }}
      >
        {formatMonetaire(montant)}
      </ListeTable.Cell>
      {hasPrelevement && (
        <>
          <ListeTable.Cell
            name="datePrelevement"
            textAlign="center"
            // @ts-expect-error
            title={datePrelevement}
            style={{ width: 175 }}
          >
            {datePrelevement ? dayjs(datePrelevement).format('DD/MM/YYYY') : '-'}
          </ListeTable.Cell>
          <ListeTable.Cell
            name="nombreFacturesPrelevees"
            // @ts-expect-error
            title={nombreFacturesPrelevees}
            style={{ width: 120 }}
          >
            {nombreFacturesPrelevees}
          </ListeTable.Cell>
        </>
      )}
      <ListeTable.Cell
        name="montantEncaisse"
        textAlign="right"
        // @ts-expect-error
        title={formatMonetaire(montantEncaisse)}
        style={{ width: 140 }}
      >
        {formatMonetaire(montantEncaisse)}
      </ListeTable.Cell>
      <ListeTable.Cell name="etat" stretched style={{ width: 200 }}>
        <Message
          className={
            etat === ETATS_CYCLE.ENCAISSEMENT_EN_COURS
              ? 'cycle-etat-en-cours'
              : etat === ETATS_CYCLE.ENCAISSEMENT_TERMINE
                ? 'cycle-etat-termine'
                : 'cycle-etat-impayes'
          }
          attached
          size="tiny"
          style={{
            margin: 0,
            border: 'none',
            boxShadow: 'none',
            borderRadius: 0,
            padding: 0,
          }}
        >
          <Message.Content>
            <Message.Header
              style={{
                flex: 1,
                overflowX: 'hidden',
                textOverflow: 'ellipsis',
                padding: '1.25em 1.5em',
                margin: 0,
                fontWeight: 500,
              }}
            >
              {etat}
            </Message.Header>
          </Message.Content>
        </Message>
      </ListeTable.Cell>
    </ListeTable.Row>
  );
};

export default CyclesListeTableRow;
